/**
 * FormField Component
 * 
 * Componente de campo de formulário reutilizável.
 * Suporta diferentes tipos de input com validação e mensagens de erro.
 * 
 * Funcionalidades:
 * - Diferentes tipos de input (text, email, number, select, etc)
 * - Validação e mensagens de erro
 * - Labels e placeholders
 * - Estados de erro e sucesso
 * - Máscaras (CPF, CNPJ, telefone, CEP)
 */

import './FormField.css'

function FormField({
  label,
  name,
  type = 'text',
  value,
  onChange,
  error = null,
  placeholder = '',
  required = false,
  disabled = false,
  options = [],
  mask = null,
  ...props
}) {
  const applyMask = (value, maskType) => {
    if (!value) return ''
    
    switch (maskType) {
      case 'cpf':
        return value.replace(/\D/g, '').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d{1,2})$/, '$1-$2')
      case 'cnpj':
        return value.replace(/\D/g, '').replace(/(\d{2})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1/$2').replace(/(\d{4})(\d)/, '$1-$2')
      case 'phone':
        return value.replace(/\D/g, '').replace(/(\d{2})(\d)/, '($1) $2').replace(/(\d{4,5})(\d{4})$/, '$1-$2')
      case 'cep':
        return value.replace(/\D/g, '').replace(/(\d{5})(\d)/, '$1-$2')
      case 'currency':
        return new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL'
        }).format(value / 100)
      default:
        return value
    }
  }

  const handleChange = (e) => {
    let newValue = e.target.value
    
    if (mask) {
      newValue = applyMask(newValue, mask)
    }
    
    onChange({
      ...e,
      target: {
        ...e.target,
        value: newValue
      }
    })
  }

  const renderInput = () => {
    switch (type) {
      case 'select':
        // Verificar se já existe uma opção vazia nas options
        const hasEmptyOption = options.some(opt => opt.value === '' || opt.value === null || opt.value === undefined)
        return (
          <select
            id={name}
            name={name}
            value={value || ''}
            onChange={handleChange}
            disabled={disabled}
            className={`form-input ${error ? 'error' : ''}`}
            {...props}
          >
            {!hasEmptyOption && <option value="">Selecione...</option>}
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        )
      
      case 'textarea':
        return (
          <textarea
            id={name}
            name={name}
            value={value || ''}
            onChange={handleChange}
            disabled={disabled}
            placeholder={placeholder}
            className={`form-input ${error ? 'error' : ''}`}
            rows={props.rows || 4}
            {...props}
          />
        )
      
      default:
        return (
          <input
            id={name}
            name={name}
            type={type}
            value={value || ''}
            onChange={handleChange}
            disabled={disabled}
            placeholder={placeholder}
            className={`form-input ${error ? 'error' : ''}`}
            {...props}
          />
        )
    }
  }

  return (
    <div className="form-field">
      {label && (
        <label htmlFor={name} className="form-label">
          {label}
          {required && <span className="required">*</span>}
        </label>
      )}
      {renderInput()}
      {error && (
        <span className="form-error">{error}</span>
      )}
    </div>
  )
}

export default FormField

